import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Phone, 
  Calendar, 
  CheckCircle, 
  Clock,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import ContactForm from '../components/shared/ContactForm';
import { companyInfo, images } from '../data/mock';

const QuotePage = () => {
  const steps = [
    {
      icon: FileText,
      title: "Compila il Form",
      description: "Inserisci i tuoi dati e descrivi brevemente il lavoro che desideri realizzare."
    },
    {
      icon: Phone,
      title: "Ti Contattiamo",
      description: "Entro 24 ore ti richiamiamo per discutere i dettagli del tuo progetto."
    },
    {
      icon: Calendar,
      title: "Sopralluogo Gratuito",
      description: "Fissiamo un appuntamento per valutare gli spazi e prendere le misure."
    },
    {
      icon: CheckCircle,
      title: "Preventivo Dettagliato",
      description: "Ricevi un preventivo completo, chiaro e senza sorprese."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="preventivo-section" className="pt-32 pb-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
                Preventivo Gratuito
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6 leading-tight">
                Richiedi un preventivo senza impegno
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Compila il form e riceverai un preventivo dettagliato entro 48 ore. 
                Sopralluogo gratuito incluso!
              </p>

              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>100% Gratuito</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Senza impegno</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span>Risposta in 24h</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href={`tel:${companyInfo.phone}`}>
                  <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50">
                    <Phone className="mr-2 w-4 h-4" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </div>

            <Card className="bg-white border-0 shadow-2xl">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Richiedi Preventivo</h2>
                <p className="text-slate-500 mb-6">Compila tutti i campi per ricevere un preventivo accurato</p>
                <ContactForm variant="quote" />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Come Funziona
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
              4 semplici passi per il tuo preventivo
            </h2>
            <p className="text-slate-600 text-lg">
              Il nostro processo è semplice e trasparente. Dalla richiesta al preventivo 
              finale, ti accompagniamo in ogni fase.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="relative">
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-amber-200" />
                  )}
                  <Card className="relative bg-white border-slate-100 shadow-lg hover:shadow-xl transition-shadow h-full">
                    <CardContent className="p-6 text-center">
                      <div className="relative inline-flex">
                        <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mb-6">
                          <IconComponent className="w-10 h-10 text-amber-600" />
                        </div>
                        <span className="absolute -top-2 -right-2 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={images.hero}
                  alt="Pavimento in parquet elegante"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-amber-100 rounded-2xl -z-10" />
            </div>

            <div>
              <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
                Le Nostre Garanzie
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Perché richiedere un preventivo a noi?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Preventivo trasparente</h3>
                    <p className="text-slate-600 text-sm">Nessun costo nascosto. Il prezzo che vedi è il prezzo che paghi.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Sopralluogo gratuito</h3>
                    <p className="text-slate-600 text-sm">Veniamo a valutare gli spazi senza alcun costo per te.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Nessun impegno</h3>
                    <p className="text-slate-600 text-sm">Sei libero di accettare o meno. Zero pressioni.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Risposta rapida</h3>
                    <p className="text-slate-600 text-sm">Ti contattiamo entro 24 ore dalla richiesta.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/contatti">
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                    Hai altre domande?
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;
